import { getMsgDeskRuntime } from '@/msgdeskRuntime'
import type { SubRenderState } from '@/msgdeskRuntime/state'
import { extractPayloadSpanJson } from '@/msgdeskRuntime/state/payloadJson'
import type { EmailMessageAddress, EmailMessageMetadata } from '@/contracts/emailMessage'
import type { MainAttachmentState } from '@/msgdeskRuntime/state'

const MSGDESK_MODEL_API_PATH = '/api/msgdesk/model'

export interface MailboxPreviewBody {
  elId: number
  codeKey: string
  isMsg: boolean
  author: string
  address: string
  subject: string
  datetime: string
  bodyHtml: string
  body: string
  avatarPath: string
  msgType: string
  from: EmailMessageAddress[]
  to: EmailMessageAddress[]
  emailMessage: EmailMessageMetadata | null
  attachments: MainAttachmentState[]
}

function clean(value: unknown) {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : ''
}

function record(value: unknown): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : {}
}

function firstClean(...values: unknown[]) {
  for (const value of values) {
    const text = clean(value)
    if (text) return text
  }
  return ''
}

function tupleAddresses(value: unknown): EmailMessageAddress[] {
  if (!Array.isArray(value)) return []
  return value.flatMap(item => {
    if (!Array.isArray(item)) return []
    const address = firstClean(item[1], item[0])
    return address ? [{ name: firstClean(item[0]), address }] : []
  })
}

function extractDataPayload(html: string, id: string) {
  return extractPayloadSpanJson(html, id) || ''
}

function bodyTextareaText(msg: Record<string, any>) {
  const value = msg.msg_inputs?.body_textarea?.[1]
  if (typeof value === 'string' || typeof value === 'number') return clean(value)
  return clean(value?.msg_text)
}

function isSubMsg(sub: SubRenderState) {
  if (typeof sub.isMsg === 'boolean') return sub.isMsg
  return sub.legacy.data?.is_msg === true || sub.legacy.data?.is_submsges_section === true
}

function showPinForSub(sub: SubRenderState) {
  const legacy = record(sub.legacy.data)
  const runtimePin = getMsgDeskRuntime('template2').params?.show_pin || '0'
  const subPin = firstClean(legacy.show_pin, legacy.showpin)
  return subPin && subPin !== '-1' ? subPin : runtimePin
}

function buildPreviewQuery(sub: SubRenderState) {
  const runtime = getMsgDeskRuntime('template2')
  const codeKey = firstClean(sub.codeKey, sub.legacy.data?.code_key, sub.legacy.data?.codekey, sub.href)
  const qs = new URLSearchParams({
    action: 'get_msg',
    nextjs: '1',
    codekey: codeKey,
    show_pin: showPinForSub(sub),
    is_msg: isSubMsg(sub) ? '1' : '0',
    android: '0',
    is_link_view: 'false',
    is_test_mode: String(runtime.isTestMode()),
    time_offset: String(new Date().getTimezoneOffset() * 60 * -1),
    local_time: String(Date.now()),
  })
  return qs
}

export function parseMailboxPreviewPayload(html: string, fallbackSub: SubRenderState): MailboxPreviewBody {
  const payloadText = extractDataPayload(html, 'mcss-msges_data-cssm') || extractDataPayload(html, 'mcss-msges_data_json-cssm')
  if (!payloadText) throw new Error('preview payload missing')

  const payload = JSON.parse(payloadText)
  const msg = record(Array.isArray(payload?.subs) ? payload.subs[0] : null)
  if (!Object.keys(msg).length) throw new Error('preview message missing')

  const codeKey = firstClean(msg.code_key, msg.codekey, fallbackSub.codeKey)
  const bodyHtml = firstClean(msg.html, msg.body_html, msg.txt_html, bodyTextareaText(msg))
  const body = firstClean(msg.text, msg.msg_text, msg.txt)
  const author = firstClean(msg.name_key, msg.namekey_subject, codeKey, fallbackSub.author, fallbackSub.codeKey)
  const facade = record(msg.facade)
  const email = record(facade.email)
  const emailMessage = email.message && typeof email.message === 'object'
    ? email.message as EmailMessageMetadata
    : null
  const attachmentItems = Array.isArray(record(facade.attachments).items)
    ? record(facade.attachments).items as Array<Record<string, unknown>>
    : []
  const attachments = attachmentItems.flatMap((attachment, index): MainAttachmentState[] => {
    const src = firstClean(attachment.src)
    if (!src) return []
    return [{
      id: firstClean(attachment.id, index) || String(index),
      src,
      thumbSrc: firstClean(attachment.thumb_src, src),
      filename: firstClean(attachment.filename),
      title: firstClean(attachment.title),
      description: firstClean(attachment.description),
      width: Number(attachment.width) || undefined,
      height: Number(attachment.height) || undefined,
      legacyIndex: index,
    }]
  })
  const from = emailMessage?.from.length ? emailMessage.from : tupleAddresses(msg.email_from)
  const to = emailMessage?.to.length ? emailMessage.to : tupleAddresses(msg.email_to)

  return {
    elId: fallbackSub.elId,
    codeKey,
    isMsg: msg.is_msg === true || msg.is_msg === 1 || msg.is_msg === '1',
    author: author || 'Unknown sender',
    address: from[0]?.address || to[0]?.address || (codeKey ? `${codeKey}@msgdesk.com` : ''),
    subject: firstClean(msg.namekey_subject, msg.subject, msg.title, fallbackSub.title) || '(No subject)',
    datetime: firstClean(msg.datetime, msg.timestart_str, msg.timestart, fallbackSub.datetime),
    bodyHtml,
    body,
    avatarPath: firstClean(msg.avatar_path, fallbackSub.avatarPath),
    msgType: firstClean(msg.msg_type, msg.msgtype),
    from,
    to,
    emailMessage,
    attachments,
  }
}

export async function fetchMailboxSubPreview(sub: SubRenderState, signal?: AbortSignal): Promise<MailboxPreviewBody> {
  const qs = buildPreviewQuery(sub)
  const codeKey = qs.get('codekey')
  if (!codeKey) throw new Error('preview code key missing')

  const res = await fetch(`${MSGDESK_MODEL_API_PATH}?${qs}`, { cache: 'no-store', signal })
  const text = await res.text()
  if (!res.ok) throw new Error(`preview HTTP ${res.status}: ${text.slice(0, 120)}`)
  if (!text || text.startsWith('to_link_')) throw new Error('preview is not a message payload')
  return parseMailboxPreviewPayload(text, sub)
}
